import React, { useRef, useEffect } from 'react';
import './RichTextEditor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  rows?: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Write your blog post content here...",
  className = "",
  rows = 10
}) => {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || document.activeElement === editor) return;

    if (editor.innerHTML !== (value || '')) {
      editor.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const executeCommand = (command: string, commandValue?: string) => {
    if (command === 'createLink') {
      const url = window.prompt('Link address', commandValue || 'https://');
      if (!url) return;
      document.execCommand(command, false, url);
    } else {
      document.execCommand(command, false, commandValue);
    }
    editorRef.current?.focus();
    handleInput();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      executeCommand('indent');
    }
  };

  const ToolbarButton: React.FC<{
    icon: string;
    command: string;
    title: string;
    value?: string;
    isActive?: boolean;
  }> = ({ icon, command, title, value, isActive = false }) => (
    <button
      type="button"
      className={`toolbar-btn ${isActive ? 'active' : ''}`}
      onClick={() => executeCommand(command, value)}
      title={title}
      onMouseDown={(e) => e.preventDefault()}
    >
      <i className={icon}></i>
    </button>
  );

  return (
    <div className="rich-text-editor">
      <div className="toolbar">
        <ToolbarButton 
          icon="fas fa-heading"
          command="formatBlock"
          value="h2"
          title="H2 Header"
        />
        <ToolbarButton 
          icon="fas fa-paragraph"
          command="formatBlock"
          value="p"
          title="Paragraph"
        />
        <ToolbarButton 
          icon="fas fa-font"
          command="bold"
          title="Bold"
        />
        <ToolbarButton 
          icon="fas fa-italic"
          command="italic"
          title="Italic"
        />
        <ToolbarButton 
          icon="fas fa-underline"
          command="underline"
          title="Underline"
        />
        <ToolbarButton 
          icon="fas fa-list"
          command="insertUnorderedList"
          title="Bullet List"
        />
        <ToolbarButton 
          icon="fas fa-list-ol"
          command="insertOrderedList"
          title="Numbered List"
        />
        <ToolbarButton 
          icon="fas fa-quote-right"
          command="formatBlock"
          value="blockquote"
          title="Quote"
        />
        <div className="toolbar-separator"></div>
        <ToolbarButton 
          icon="fas fa-align-left"
          command="justifyLeft"
          title="Align Left"
        />
        <ToolbarButton 
          icon="fas fa-align-center"
          command="justifyCenter"
          title="Align Center"
        />
        <ToolbarButton 
          icon="fas fa-align-right"
          command="justifyRight"
          title="Align Right"
        />
        <ToolbarButton 
          icon="fas fa-align-justify"
          command="justifyFull"
          title="Justify Text"
        />
        <div className="toolbar-separator"></div>
        <ToolbarButton 
          icon="fas fa-link"
          command="createLink"
          value="https://"
          title="Link"
        />
        <ToolbarButton 
          icon="fas fa-undo"
          command="undo"
          title="Undo"
        />
        <ToolbarButton 
          icon="fas fa-redo"
          command="redo"
          title="Redo"
        />
      </div>
      <div
        ref={editorRef}
        contentEditable
        dir="ltr"
        className={`editor-content ${className}`}
        style={{ minHeight: `${rows * 1.5}em`, direction: 'ltr', textAlign: 'left' }}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onBlur={handleInput}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
    </div>
  );
};

export default RichTextEditor;



